'use client';

import * as React from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { IconChevronLeft } from 'icon/Arrows/chevron-left';
import { IconChevronRight } from 'icon/Arrows/chevron-right';
import { IconSwitchVertical01 } from 'icon/Arrows/switch-vertical-01';
import { IconFilterLines } from 'icon/General/filter-lines';
import { IconSearchSm } from 'icon/General/search-sm';
import { fetchCommentRead, retryCommentRead } from 'lib/comment-read';
import type { MyComments } from 'lib/comments';
import { useFormatter, useTranslations } from 'next-intl';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'ui';
import { CommentContent } from './comment-content';
import { COMMENT_INPUT } from './comment-editor';
import { CommentErrorMessage } from './comment-identity';

export function MyCommentsPanel({
  postId,
  manage,
  identity,
}: {
  postId: string;
  manage: boolean;
  identity: string;
}) {
  const t = useTranslations('Blog.Comments');
  const format = useFormatter();
  const [filters, setFilters] = React.useState({
    scope: 'all',
    domain: '',
    search: '',
    from: '',
    to: '',
    sort: 'newest',
  });
  const [search, setSearch] = React.useState('');
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(filters);
  const [page, setPage] = React.useState(0);
  const prefix = React.useId();
  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      setFilters((previous) =>
        previous.search === search ? previous : { ...previous, search },
      );
      setPage(0);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [search]);
  const filterCount =
    Number(filters.scope === 'current') +
    Number(!!filters.domain) +
    Number(!!(filters.from || filters.to));
  const sortLabel =
    filters.sort === 'newest'
      ? t('Newest')
      : filters.sort === 'oldest'
        ? t('Oldest')
        : t('Domain');
  const query = useQuery({
    queryKey: ['my-comments', identity, postId, manage, filters, page],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({
        view: 'mine',
        postId,
        manage: String(manage),
        page: String(page),
        ...filters,
      });
      if (!filters.from) params.delete('from');
      if (!filters.to) params.delete('to');
      return fetchCommentRead<MyComments>(
        `/.quotion/api/comments?${params}`,
        signal,
      );
    },
    placeholderData: keepPreviousData,
    retry: retryCommentRead,
  });
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1">
        <form
          role="search"
          className="relative min-w-0 flex-1"
          onSubmit={(event) => {
            event.preventDefault();
            setFilters((previous) => ({ ...previous, search }));
            setPage(0);
          }}
        >
          <IconSearchSm
            aria-hidden="true"
            className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-tertiary"
          />
          <label htmlFor={`${prefix}-search`} className="sr-only">
            {t('SearchComments')}
          </label>
          <input
            id={`${prefix}-search`}
            name="search"
            type="search"
            placeholder={t('SearchComments')}
            className={`${COMMENT_INPUT} h-10 ps-9`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            maxLength={100}
          />
        </form>
        <Popover
          open={filterOpen}
          onOpenChange={(open) => {
            if (open) setDraft(filters);
            setFilterOpen(open);
          }}
        >
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={`relative shrink-0 rounded-md ${filterCount ? 'bg-secondary text-primary' : 'text-tertiary'}`}
              aria-label={t('Filters')}
              title={t('Filters')}
            >
              <IconFilterLines aria-hidden="true" className="size-4" />
              {filterCount > 0 && (
                <span className="absolute end-0.5 top-0.5 flex size-4 items-center justify-center rounded-full bg-brand-600 text-[10px] font-medium text-white">
                  {filterCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            collisionPadding={24}
            className="w-80 max-w-[calc(100vw-3rem)] rounded-lg border-secondary bg-primary text-primary"
            aria-label={t('Filters')}
          >
            <form
              className="space-y-3"
              onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                setFilters((previous) => ({
                  ...previous,
                  scope: String(form.get('scope')),
                  domain: String(form.get('domain')).trim(),
                  from: String(form.get('from')),
                  to: String(form.get('to')),
                }));
                setPage(0);
                setFilterOpen(false);
              }}
            >
              <label className="block space-y-1.5 text-xs text-tertiary">
                <span>{t('Site')}</span>
                <select
                  name="scope"
                  aria-label={t('Site')}
                  className={`${COMMENT_INPUT} h-10`}
                  value={draft.scope}
                  onChange={(event) =>
                    setDraft((previous) => ({
                      ...previous,
                      scope: event.target.value,
                    }))
                  }
                >
                  <option value="all">{t('AllSites')}</option>
                  <option value="current">{t('CurrentSite')}</option>
                </select>
              </label>
              <label className="block space-y-1.5 text-xs text-tertiary">
                <span>{t('Domain')}</span>
                <input
                  name="domain"
                  className={`${COMMENT_INPUT} h-10`}
                  placeholder="example.com"
                  maxLength={253}
                  value={draft.domain}
                  onChange={(event) =>
                    setDraft((previous) => ({
                      ...previous,
                      domain: event.target.value,
                    }))
                  }
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="min-w-0 space-y-1.5 text-xs text-tertiary">
                  <span>{t('FromDate')}</span>
                  <input
                    name="from"
                    type="date"
                    className={`${COMMENT_INPUT} h-10 min-w-0`}
                    max={draft.to || undefined}
                    value={draft.from}
                    onChange={(event) =>
                      setDraft((previous) => ({
                        ...previous,
                        from: event.target.value,
                      }))
                    }
                  />
                </label>
                <label className="min-w-0 space-y-1.5 text-xs text-tertiary">
                  <span>{t('ToDate')}</span>
                  <input
                    name="to"
                    type="date"
                    className={`${COMMENT_INPUT} h-10 min-w-0`}
                    min={draft.from || undefined}
                    value={draft.to}
                    onChange={(event) =>
                      setDraft((previous) => ({
                        ...previous,
                        to: event.target.value,
                      }))
                    }
                  />
                </label>
              </div>
              <div className="flex items-center justify-between gap-2 pt-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-tertiary"
                  onClick={() => {
                    setFilters((previous) => ({
                      ...previous,
                      scope: 'all',
                      domain: '',
                      from: '',
                      to: '',
                    }));
                    setPage(0);
                    setFilterOpen(false);
                  }}
                >
                  {t('Reset')}
                </Button>
                <Button type="submit" size="sm">
                  {t('ApplyFilters')}
                </Button>
              </div>
            </form>
          </PopoverContent>
        </Popover>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 rounded-md text-tertiary"
              aria-label={t('Sort')}
              title={`${t('Sort')}: ${sortLabel}`}
            >
              <IconSwitchVertical01 aria-hidden="true" className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="border-secondary bg-primary text-primary"
          >
            <DropdownMenuRadioGroup
              value={filters.sort}
              onValueChange={(sort) => {
                setFilters((previous) => ({ ...previous, sort }));
                setPage(0);
              }}
            >
              <DropdownMenuRadioItem value="newest">
                {t('Newest')}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="oldest">
                {t('Oldest')}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="domain">
                {t('Domain')}
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {query.isPending && (
        <div className="space-y-4 py-2" role="status" aria-label={t('Loading')}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded bg-secondary motion-reduce:animate-none"
            />
          ))}
        </div>
      )}
      {query.error && <CommentErrorMessage code={query.error.message} />}
      {query.data && (
        <>
          <div
            className={`max-h-[55dvh] overflow-y-auto divide-y divide-secondary ${query.isPlaceholderData ? 'opacity-50' : ''}`}
            aria-busy={query.isFetching}
          >
            {query.data.comments.map((comment) => (
              <article key={comment.id} className="min-w-0 py-4 first:pt-0">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-tertiary">
                  <span className="break-all">{comment.domain}</span>
                  <time dateTime={comment.createdAt}>
                    {format.dateTime(new Date(comment.createdAt), {
                      dateStyle: 'medium',
                    })}
                  </time>
                </div>
                <div className="mb-2 text-sm font-medium">
                  {comment.url ? (
                    <a
                      href={comment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="break-words text-secondary hover:text-primary hover:underline"
                    >
                      {comment.postTitle || t('PrivatePost')}
                    </a>
                  ) : (
                    <span className="text-tertiary">
                      {t('UnavailablePost')}
                    </span>
                  )}
                  {comment.reply && (
                    <span className="ms-2 text-xs font-normal text-tertiary">
                      {t('Reply')}
                    </span>
                  )}
                </div>
                {comment.document && (
                  <CommentContent document={comment.document} />
                )}
              </article>
            ))}
            {query.data.total === 0 && (
              <p className="py-6 text-center text-sm text-tertiary">
                {t('NoMatchingComments')}
              </p>
            )}
          </div>
          <nav
            aria-label={t('Pagination')}
            className="flex items-center justify-between gap-3 border-t border-secondary pt-2"
          >
            <span className="text-xs text-tertiary" role="status">
              {t('Results', { count: query.data.total })}
            </span>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="rounded-md text-secondary"
                aria-label={t('Previous')}
                title={t('Previous')}
                disabled={page === 0 || query.isFetching}
                onClick={() => setPage((value) => value - 1)}
              >
                <IconChevronLeft aria-hidden="true" className="size-4" />
              </Button>
              <span className="text-xs tabular-nums text-tertiary">
                {t('PageOf', {
                  page: page + 1,
                  total: Math.max(
                    1,
                    Math.ceil(query.data.total / query.data.pageSize),
                  ),
                })}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="rounded-md text-secondary"
                aria-label={t('Next')}
                title={t('Next')}
                disabled={
                  (page + 1) * query.data.pageSize >= query.data.total ||
                  query.isFetching
                }
                onClick={() => setPage((value) => value + 1)}
              >
                <IconChevronRight aria-hidden="true" className="size-4" />
              </Button>
            </div>
          </nav>
        </>
      )}
    </div>
  );
}
